import {
  QueryConstraint,
  QuerySnapshot,
  addDoc,
  collection,
  getDocs,
  limit,
  query,
  serverTimestamp,
  where
} from "firebase/firestore"
import { IGenericError, IMarkScheme, IMarkSchemeBase, IMarkSchemeRaw } from "../types"
import { firebaseDb } from "./init"

const markSchemesCollectionName = 'markingSchemes'

const markSchemesCollectionRef = collection(
  firebaseDb,
  markSchemesCollectionName,
)

export async function createMarkScheme(
  markScheme: IMarkSchemeBase,
): Promise<string> {
  try {
    const isMarkSchemesIdUnique: boolean = await checkMarkSchemeIdIsUnique(markScheme)

    if (!isMarkSchemesIdUnique) {
      throw new Error(
        `Mark scheme with markSchemeId: "${markScheme.markSchemeId}" already exists`,
      )
    }

    const document = await addDoc(markSchemesCollectionRef, {
      ...markScheme,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })

    return document.id
  } catch (error: unknown) {
    console.error(
      `error while trying to create mark scheme :>> `,
      (error as IGenericError)?.message || error,
      markScheme,
    )

    throw new Error(
      (error as IGenericError)?.message ||
      `Unexpected error while trying to create mark scheme`,
    )
  }
}

export async function getMarkSchemeByMarkSchemeIdId(
  markSchemeId: string,
): Promise<IMarkScheme | undefined> {
  try {
    const q = query(markSchemesCollectionRef, where('markSchemeId', '==', markSchemeId), limit(1))
    const querySnapshot = await getDocs(q)

    if (querySnapshot.empty) {
      return undefined
    }

    const docSnap = querySnapshot.docs[0]
    const data = docSnap.data() as IMarkSchemeRaw

    const doc: IMarkScheme = {
      id: docSnap.id,
      ...data,
    }

    return doc
  } catch (error: unknown) {
    console.error(
      `error while trying to get mark scheme by markSchemeId: ${markSchemeId} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
      `Unexpected error while trying to get mark scheme by markSchemeId`,
    )
  }
}

async function checkMarkSchemeIdIsUnique(
  markScheme: IMarkSchemeBase,
): Promise<boolean> {
  const queryConstraints: QueryConstraint[] = [
    where('markSchemeId', '==', markScheme.markSchemeId),
  ]

  const markSchemes: IMarkScheme[] = await getMarkSchemes(
    queryConstraints,
  )

  return !!markSchemes.length ? false : true
}

export async function getMarkSchemes(
  queryConstraints: QueryConstraint[] = [],
): Promise<IMarkScheme[]> {
  try {
    const q = query(markSchemesCollectionRef, ...queryConstraints)
    const querySnapshot = (await getDocs(
      q,
    )) as unknown as QuerySnapshot<IMarkSchemeRaw>

    let documents: IMarkScheme[] = []

    querySnapshot.forEach((snapshot) => {
      documents.push({
        id: snapshot.id,
        ...snapshot.data(),
      })
    })

    return documents
  } catch (error: unknown) {
    console.error(
      `error while trying to get mark scheme :>> `,
      (error as IGenericError)?.message || error,
      queryConstraints,
    )

    throw new Error(
      (error as IGenericError)?.message ||
      `Unexpected error while trying to get mark scheme`,
    )
  }
}
